import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {getWindow} from 'ssr-window';
import IconCheck from '@spothero/icons/check';
import DOMUtils from '@spothero/utils/dom';
import Image from 'common/image/Image';
import withConfig from 'config/withConfig';
import Card from 'common/card/Card';
import Link from 'router/Link';
import AirportSpotSummary from 'common/airport/AirportSpotSummary';
import SpotUtils from 'utils/spot';
import StarRatingWithCategory from 'common/star-rating/StarRatingWithCategory';
import SearchAirportOfferSchema from 'common/airport/SearchAirportOfferSchema';

const window = getWindow();

class SearchAirportCard extends Component {
    static propTypes = {
        spot: PropTypes.object.isRequired,
        rank: PropTypes.number,
        starts: PropTypes.string,
        ends: PropTypes.string,
        goToCheckoutDirectly: PropTypes.bool,
        staticBaseUrl: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
    };
    static defaultProps = {
        priceIsDefault: true,
    };

    constructor(props) {
        super(props);

        const {
            spot: {
                facility: {
                    spot_details_url: spotDetailsUrl = '',
                    logos,
                    images,
                } = {},
                selectedRate: {fullUrl},
            } = {},
            starts,
            ends,
        } = props;

        this._spotDetailsUrl = `/${spotDetailsUrl}/?starts=${starts}&ends=${ends}&airport=true`;
        this._checkoutUrl = fullUrl;
        this._logoId =
            logos && logos.length > 0
                ? logos[0].id
                : images && images.length > 0
                ? images[0].id
                : null;
    }

    _onMouseEnterSpot = () => {
        const {
            spot: {spotId, latitude},
        } = this.props;

        DOMUtils.sendCustomEvent('spotHover', {
            spotId,
            latitude,
            hovered: true,
            activeMarker: true,
        });
    };

    _onMouseLeaveSpot = () => {
        const {
            spot: {spotId, latitude},
        } = this.props;

        DOMUtils.sendCustomEvent('spotHover', {
            spotId,
            latitude,
            hovered: false,
            activeMarker: false,
        });
    };

    _onResultClick = evt => {
        evt.preventDefault();
        evt.stopPropagation();

        const {rank, goToCheckoutDirectly, onClick} = this.props;

        onClick(rank);

        window.open(
            goToCheckoutDirectly ? this._checkoutUrl : this._spotDetailsUrl,
            '_blank'
        );

        // Returning false from an anchor tag prevents the default behavior of following the href
        // In this instance, the href and this._url are for SEO purposes only
        return false;
    };

    _renderPrice = (price, currencyType) => {
        const displayPriceFormatted = SpotUtils.formatRatePrice({
            price,
            currencyType,
        });

        return (
            <span
                className="price"
                dangerouslySetInnerHTML={{__html: displayPriceFormatted}}
            />
        );
    };

    _renderFreeCancellation() {
        const {
            spot: {
                facility: {cancellation_allowed: cancellationAllowed},
            },
        } = this.props;

        // cancellationAllowed has 3 possible values: 'customer', 'hero', and 'no'
        if (cancellationAllowed === 'no') {
            return;
        } else {
            return (
                <div className="SearchAirportCard-cancellation">
                    <IconCheck />
                    <span className="SearchAirportCard-cancellation-text">
                        Free Cancellation
                    </span>
                </div>
            );
        }
    }

    render() {
        const {
            starts,
            ends,
            spot: {
                facility,
                selectedRate: {
                    display_price: dailyPrice,
                    currency_type: currencyType,
                    unavailable: isUnavailable,
                    unavailable_reason: unavailableReason,
                },
            },
            staticBaseUrl,
            goToCheckoutDirectly,
        } = this.props;
        const {
            title,
            tag,
            rating_info: {
                number_of_ratings: numberOfReviews,
                star_rating: starRating,
            },
            distance: distanceInMiles,
            airport,
            amenities_full: amenitiesList,
        } = facility;
        const bookButtonClasses = classNames(
            'Button',
            'SearchAirportCard-book-now',
            {'SearchAirportCard-book-now-unavailable': isUnavailable}
        );
        const unavailableTag = Boolean(unavailableReason) && (
            <div className="SearchAirportCard-unavailable-tag">
                * {unavailableReason}
            </div>
        );
        const logoProps = {
            width: 144,
            height: 144,
            alt: `Photo of ${title}`,
            ...(this._logoId
                ? {cloudinaryImageId: this._logoId}
                : {src: `${staticBaseUrl}img/missing-image.png`}),
        };

        return (
            <section
                className="SearchAirportCard"
                itemProp="item"
                itemScope
                itemType="http://schema.org/Product"
                onMouseEnter={this._onMouseEnterSpot}
                onMouseLeave={this._onMouseLeaveSpot}
            >
                <link itemProp="url" href={this._checkoutUrl} />
                <Card heading={tag}>
                    <div className="SearchAirportCard-top-content">
                        <div className="SearchAirportCard-photo">
                            <Image {...logoProps} />
                        </div>
                        <div className="SearchAirportCard-details">
                            <h2 itemProp="name">{unescape(title)}</h2>
                            <StarRatingWithCategory
                                numberOfReviews={numberOfReviews}
                                starRating={starRating}
                            />
                            {tag && (
                                <div className="SearchAirportCard-tags-container">
                                    <span className="SearchAirportCard-tag">
                                        {tag}
                                    </span>
                                </div>
                            )}
                            {unavailableTag}
                            <AirportSpotSummary
                                data={airport}
                                amenities={amenitiesList}
                                distanceInMiles={distanceInMiles}
                            />
                            {this._renderFreeCancellation()}
                        </div>
                    </div>
                    <div className="SearchAirportCard-bottom-content">
                        <AirportSpotSummary
                            data={airport}
                            amenities={amenitiesList}
                            distanceInMiles={distanceInMiles}
                        />
                        {this._renderFreeCancellation()}
                    </div>
                    <Link
                        className="SearchAirportCard-click-overlay"
                        to={this._spotDetailsUrl}
                        itemProp="url"
                        target="_blank"
                        onClick={this._onResultClick}
                    />
                    <div className="SearchAirportCard-price-container">
                        <div className="SearchAirportCard-daily-price">
                            <Link
                                className="SearchAirportCard-daily-price-link"
                                to={this._spotDetailsUrl}
                                target="_blank"
                                onClick={this._onResultClick}
                            >
                                {this._renderPrice(dailyPrice, currencyType)}
                                <span>/day</span>
                            </Link>
                        </div>

                        <SearchAirportOfferSchema
                            facilityTitle={title}
                            displayPrice={dailyPrice}
                            currencyType={currencyType}
                            starts={starts}
                            ends={ends}
                        />
                    </div>
                    <div className="SearchAirportCard-book-now-container">
                        {unavailableTag}
                        <Link
                            className={bookButtonClasses}
                            to={
                                goToCheckoutDirectly
                                    ? this._checkoutUrl
                                    : this._spotDetailsUrl
                            }
                            itemProp="url"
                            onClick={this._onResultClick}
                        >
                            Book Now
                        </Link>
                        {this._renderFreeCancellation()}
                    </div>
                </Card>
            </section>
        );
    }
}

export default withConfig(['staticBaseUrl'])(SearchAirportCard);
